import { useState, useEffect } from 'react'
import { Users, MapPin, Calendar, MessageCircle, Heart, Shield, Zap } from 'lucide-react'
import { PublicLayout } from '../components/layouts/Layout'
import { Button, Card } from '../components/common/UI'

const MobileScreenshots = () => {
  const [currentImage, setCurrentImage] = useState(0)
  
  const screenshots = [
    { title: "Events", image: "/assets/events-screenshot.png" },
    { title: "Trips", image: "/assets/trips-screenshot.png" },
    { title: "Find Friends", image: "/assets/friends-screenshot.png" },
    { title: "Welcome", image: "/assets/welcome-screenshot.png" }
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % screenshots.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [screenshots.length])

  return (
    <div className="relative flex justify-center lg:justify-end">
      <div className="relative">
        <div className="bg-white rounded-[40px] shadow-xl p-3 w-80 h-[600px] border border-gray-200">
          <div className="bg-gray-900 rounded-[32px] h-full flex flex-col overflow-hidden">
            <div className="flex justify-center pt-4 pb-2">
              <div className="w-32 h-5 bg-gray-800 rounded-full"></div>
            </div>
            <div className="flex-1 bg-white rounded-t-[28px] mt-1 overflow-hidden">
              <img src={screenshots[currentImage].image} alt={screenshots[currentImage].title} className="w-full h-full object-cover transition-opacity duration-500" />
            </div>
          </div>
        </div>
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {screenshots.map((_, index) => (
            <button key={index} onClick={() => setCurrentImage(index)}
              className={`w-2 h-2 rounded-full transition-colors ${index === currentImage ? 'bg-blue-600' : 'bg-gray-300'}`} />
          ))}
        </div>
      </div>
    </div>
  )
}

const HeroSection = () => {
  const handleDownload = (platform: 'ios' | 'android') => {
    window.location.href = platform === 'ios' 
      ? 'https://apps.apple.com/us/app/blasttime/id6739508048'
      : 'https://play.google.com/store/apps/details?id=com.xupoli.blasttimev4&hl=en'
  }

  return (
    <section className="relative bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div className="text-center lg:text-left order-2 lg:order-1">
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full text-blue-700 text-sm font-medium mb-6">
              <Zap className="w-4 h-4 mr-2" />
              Connect • Explore • Experience
            </div>
            <div className="flex items-center justify-center lg:justify-start mb-6">
              <img src="/assets/logo.png" alt="BlastTime Logo" className="w-12 h-12 mr-4" />
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 leading-tight">
                <span className="block text-blue-600">BlastTime</span>
              </h1>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-700 mb-6">Where Connections Happen</h2>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              The social app that brings people together for real experiences. Find friends, discover events, join communities, and share adventures in your city.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8 lg:mb-12">
              <Button size="lg" className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 font-semibold px-6 lg:px-8 py-3 lg:py-4 text-base lg:text-lg"
                onClick={() => handleDownload('ios')}>Download on iOS</Button>
              <Button variant="outline" size="lg" className="w-full sm:w-auto border-gray-300 text-gray-700 hover:bg-gray-50 px-6 lg:px-8 py-3 lg:py-4 text-base lg:text-lg"
                onClick={() => handleDownload('android')}>Download on Android</Button>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <MobileScreenshots />
          </div>
        </div>
      </div>
    </section>
  )
}

const FeaturesSection = () => (
  <section className="py-12 lg:py-20 bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12 lg:mb-16">
        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Everything You Need to Connect</h2>
        <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
          BlastTime brings together all the tools you need to build meaningful connections and create lasting memories
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {[
          { icon: MessageCircle, color: 'bg-blue-100 text-blue-600', title: 'Connect', desc: 'Meet new people and reconnect with friends through our smart matching system' },
          { icon: MapPin, color: 'bg-green-100 text-green-600', title: 'Explore', desc: 'Discover what\'s happening around you with location-based features' },
          { icon: Calendar, color: 'bg-purple-100 text-purple-600', title: 'Events', desc: 'Create and join events, from casual hangouts to organized activities' },
          { icon: Users, color: 'bg-orange-100 text-orange-600', title: 'Groups', desc: 'Join communities based on your interests, hobbies, and passions' },
          { icon: Heart, color: 'bg-pink-100 text-pink-600', title: 'Experiences', desc: 'Share memorable moments through photos, stories, and real-time updates' },
          { icon: Shield, color: 'bg-indigo-100 text-indigo-600', title: 'Safety First', desc: 'Built-in safety features, verification systems, and privacy controls' }
        ].map((feature, i) => (
          <Card key={i} className="text-center p-6 lg:p-8 border-0 shadow-sm" hover>
            <div className={`w-16 h-16 ${feature.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
              <feature.icon className="w-8 h-8" />
            </div>
            <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
            <p className="text-gray-600">{feature.desc}</p>
          </Card>
        ))}
      </div>
    </div>
  </section>
)

const HowItWorksSection = () => (
  <section className="py-12 lg:py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12 lg:mb-16">
        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">How BlastTime Works</h2>
        <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
          Getting started is simple. Create your profile and start connecting in minutes.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
        {[
          { num: '1', color: 'bg-blue-600', title: 'Create Your Profile', desc: 'Sign up with your phone number, add your interests, and customize your profile' },
          { num: '2', color: 'bg-green-600', title: 'Discover & Connect', desc: 'Find people, events, and groups nearby. Send connection requests and start conversations' },
          { num: '3', color: 'bg-purple-600', title: 'Meet & Experience', desc: 'Plan meetups, join events, share rides, and create memorable experiences together' }
        ].map((step) => (
          <div key={step.num} className="text-center">
            <div className={`w-16 lg:w-20 h-16 lg:h-20 ${step.color} rounded-full flex items-center justify-center mx-auto mb-6`}>
              <span className="text-xl lg:text-2xl font-bold text-white">{step.num}</span>
            </div>
            <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mb-4">{step.title}</h3>
            <p className="text-gray-600">{step.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
)

const CTASection = () => {
  const handleDownload = (platform: 'ios' | 'android') => {
    window.location.href = platform === 'ios' 
      ? 'https://apps.apple.com/us/app/blasttime/id6739508048'
      : 'https://play.google.com/store/apps/details?id=com.xupoli.blasttimev4&hl=en'
  }

  return (
    <section className="py-12 lg:py-20 bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">Ready to Start Connecting?</h2>
        <p className="text-lg lg:text-xl text-gray-300 mb-8 lg:mb-10 max-w-2xl mx-auto">
          Join thousands of users using BlastTime to connect with friends and discover amazing experiences
        </p>
        <div className="flex flex-col sm:flex-row gap-4 lg:gap-6 justify-center">
          <Button size="lg" className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 font-semibold px-6 lg:px-8 py-3 lg:py-4"
            onClick={() => handleDownload('ios')}>Download on iOS</Button>
          <Button variant="outline" size="lg" className="w-full sm:w-auto border-gray-500 text-gray-300 hover:bg-gray-800 px-6 lg:px-8 py-3 lg:py-4"
            onClick={() => handleDownload('android')}>Download on Android</Button>
        </div>
        <p className="text-gray-400 text-sm mt-6">Free to download • Available on iOS & Android • Connect today</p>
      </div>
    </section>
  )
}

export default function Landing() {
  return (
    <PublicLayout>
      <HeroSection />
      <FeaturesSection />
      <HowItWorksSection />
      <CTASection />
    </PublicLayout>
  )
}